import { useState, useEffect } from 'react'
import { FiPlus, FiEdit2, FiTrash2, FiX, FiClock, FiInfo } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../config/api'
import { useEmpresaGestion } from '../contexts/EmpresaGestionContext'
import { useAuth } from '../contexts/AuthContext'
import { PageHeader, Btn } from '../ui'

const DIAS_SEMANA = [
  { iso: 1, nombre: 'L', largo: 'Lunes' },
  { iso: 2, nombre: 'M', largo: 'Martes' },
  { iso: 3, nombre: 'X', largo: 'Miércoles' },
  { iso: 4, nombre: 'J', largo: 'Jueves' },
  { iso: 5, nombre: 'V', largo: 'Viernes' },
  { iso: 6, nombre: 'S', largo: 'Sábado' },
  { iso: 7, nombre: 'D', largo: 'Domingo' }
]

const HORAS_POR_DIA = [
  { key: 'horas_lunes',     num: 1, label: 'Lunes' },
  { key: 'horas_martes',    num: 2, label: 'Martes' },
  { key: 'horas_miercoles', num: 3, label: 'Miércoles' },
  { key: 'horas_jueves',    num: 4, label: 'Jueves' },
  { key: 'horas_viernes',   num: 5, label: 'Viernes' },
  { key: 'horas_sabado',    num: 6, label: 'Sábado' },
  { key: 'horas_domingo',   num: 7, label: 'Domingo' },
]

const EMPTY_FORM = {
  nombre: '', descripcion: '',
  dias_laborables: '1,2,3,4,5',
  hora_entrada_min: '07:00', hora_entrada_max: '10:00',
  hora_salida_min: '15:00', hora_salida_max: '21:00',
  pausa_obligatoria: false, duracion_pausa_min: 30,
  umbral_horas_extra: 30,
  horas_lunes: '', horas_martes: '', horas_miercoles: '',
  horas_jueves: '', horas_viernes: '', horas_sabado: '', horas_domingo: ''
}

export default function GestionCalendarios() {
  const { empleado } = useAuth()
  const { empresaId: empresaGestionId, esGestion } = useEmpresaGestion()

  const [calendarios, setCalendarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [editando, setEditando] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [diasSeleccionados, setDiasSeleccionados] = useState([1, 2, 3, 4, 5])
  // Punto 3 — días (ISO) en los que la pausa es obligatoria.
  const [diasPausaObligatoria, setDiasPausaObligatoria] = useState([1, 2, 3, 4, 5])

  const buildUrl = (path) => {
    const sep = path.includes('?') ? '&' : '?'
    return esGestion && empresaGestionId ? `${API_URL}${path}${sep}empresa_id=${empresaGestionId}` : `${API_URL}${path}`
  }
  const token = () => localStorage.getItem('token')
  const headers = () => ({ 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' })

  useEffect(() => { cargarCalendarios() }, [empresaGestionId])

  const cargarCalendarios = async () => {
    setLoading(true)
    try {
      const r = await fetch(buildUrl('/calendarios-laborales'), { headers: headers() })
      if (r.ok) setCalendarios(await r.json())
    } catch { toast.error('Error al cargar calendarios') }
    finally { setLoading(false) }
  }

  const abrirModal = (cal = null) => {
    setEditando(cal)
    if (cal) {
      const dias = (cal.dias_laborables || '1,2,3,4,5').split(',').map(Number)
      setDiasSeleccionados(dias)
      // Días de pausa obligatoria: del campo nuevo; si está vacío pero el
      // calendario tenía la pausa marcada (legacy), se asume todos los laborables.
      let diasPausa = (cal.dias_pausa_obligatoria || '').split(',').map(Number).filter(Boolean)
      if (diasPausa.length === 0 && cal.pausa_obligatoria) diasPausa = dias
      setDiasPausaObligatoria(diasPausa)
      setForm({
        nombre: cal.nombre, descripcion: cal.descripcion || '',
        dias_laborables: cal.dias_laborables || '1,2,3,4,5',
        hora_entrada_min: cal.hora_entrada_min || '07:00',
        hora_entrada_max: cal.hora_entrada_max || '10:00',
        hora_salida_min: cal.hora_salida_min || '15:00',
        hora_salida_max: cal.hora_salida_max || '21:00',
        pausa_obligatoria: !!cal.pausa_obligatoria,
        duracion_pausa_min: cal.duracion_pausa_min || 30,
        umbral_horas_extra: cal.umbral_horas_extra != null ? cal.umbral_horas_extra : 30,
        horas_lunes:     cal.horas_lunes    != null ? String(cal.horas_lunes)    : '',
        horas_martes:    cal.horas_martes   != null ? String(cal.horas_martes)   : '',
        horas_miercoles: cal.horas_miercoles != null ? String(cal.horas_miercoles) : '',
        horas_jueves:    cal.horas_jueves   != null ? String(cal.horas_jueves)   : '',
        horas_viernes:   cal.horas_viernes  != null ? String(cal.horas_viernes)  : '',
        horas_sabado:    cal.horas_sabado   != null ? String(cal.horas_sabado)   : '',
        horas_domingo:   cal.horas_domingo  != null ? String(cal.horas_domingo)  : ''
      })
    } else {
      setDiasSeleccionados([1, 2, 3, 4, 5])
      setDiasPausaObligatoria([1, 2, 3, 4, 5])
      setForm(EMPTY_FORM)
    }
    setModal(true)
  }

  const toggleDia = (iso) => {
    setDiasSeleccionados(prev => {
      const next = prev.includes(iso) ? prev.filter(d => d !== iso) : [...prev, iso].sort((a, b) => a - b)
      setForm(f => ({ ...f, dias_laborables: next.join(',') }))
      // Si un día deja de ser laborable, tampoco puede tener pausa obligatoria.
      if (!next.includes(iso)) setDiasPausaObligatoria(p => p.filter(d => d !== iso))
      return next
    })
  }

  // Punto 3 — marca/desmarca la pausa obligatoria para un día (solo laborables).
  const toggleDiaPausa = (iso) => {
    if (!diasSeleccionados.includes(iso)) return
    setDiasPausaObligatoria(prev =>
      prev.includes(iso) ? prev.filter(d => d !== iso) : [...prev, iso].sort((a, b) => a - b)
    )
  }

  const guardar = async (e) => {
    e.preventDefault()
    if (diasSeleccionados.length === 0) { toast.error('Selecciona al menos un día laborable'); return }
    // El backend deriva pausa_obligatoria de dias_pausa_obligatoria.
    const body = {
      ...form,
      dias_laborables: diasSeleccionados.join(','),
      dias_pausa_obligatoria: diasPausaObligatoria.join(',')
    }
    try {
      const url = editando ? buildUrl(`/calendarios-laborales/${editando.id}`) : buildUrl('/calendarios-laborales')
      const r = await fetch(url, { method: editando ? 'PUT' : 'POST', headers: headers(), body: JSON.stringify(body) })
      if (r.ok) { toast.success(editando ? 'Calendario actualizado' : 'Calendario creado'); setModal(false); cargarCalendarios() }
      else { const err = await r.json(); toast.error(err.error || 'Error al guardar') }
    } catch { toast.error('Error de conexión') }
  }

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este calendario laboral?')) return
    try {
      const r = await fetch(buildUrl(`/calendarios-laborales/${id}`), { method: 'DELETE', headers: headers() })
      if (r.ok) { toast.success('Calendario eliminado'); cargarCalendarios() }
      else { const err = await r.json(); toast.error(err.error || 'Error al eliminar') }
    } catch { toast.error('Error de conexión') }
  }

  const formatDias = (str) => {
    if (!str) return '—'
    const nums = str.split(',').map(Number)
    return DIAS_SEMANA.filter(d => nums.includes(d.iso)).map(d => d.nombre).join(' ')
  }

  return (
    <div className="space-y-6">
      <PageHeader
        num="§ K1"
        kicker="Calendarios"
        title="Horarios y"
        italic="jornadas."
        sub="Define horarios, días laborables y reglas de jornada."
        actions={<Btn variant="primary" size="sm" onClick={() => abrirModal()}><FiPlus size={14} /> Nuevo calendario</Btn>}
        className="!px-0 !pt-0 !pb-5 !border-0"
      />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-40 bg-[var(--bg-2)] rounded-lg animate-pulse" />)}
        </div>
      ) : calendarios.length === 0 ? (
        <div className="card p-12 text-center text-[var(--ink-4)]">
          <FiClock size={40} className="mx-auto mb-3 opacity-30" />
          <p>No hay calendarios laborales. Crea el primero.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {calendarios.map(cal => (
            <div key={cal.id} className="card p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-[var(--ink)]">{cal.nombre}</h3>
                  {cal.descripcion && <p className="text-xs text-[var(--ink-3)] mt-0.5">{cal.descripcion}</p>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => abrirModal(cal)} className="p-1.5 text-[var(--ink-4)] hover:text-blue-500 rounded"><FiEdit2 size={15} /></button>
                  <button onClick={() => eliminar(cal.id)} className="p-1.5 text-[var(--ink-4)] hover:text-red-500 rounded"><FiTrash2 size={15} /></button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-[var(--ink-3)] w-24 flex-shrink-0">Días:</span>
                  <div className="flex gap-1">
                    {DIAS_SEMANA.map(d => {
                      const activo = (cal.dias_laborables || '').split(',').map(Number).includes(d.iso)
                      return (
                        <span key={d.iso} className={`w-6 h-6 rounded text-xs font-medium flex items-center justify-center ${activo ? 'bg-blue-500 text-white' : 'bg-[var(--bg-2)] text-[var(--ink-4)]'}`}>
                          {d.nombre}
                        </span>
                      )
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[var(--ink-3)] w-24 flex-shrink-0">Entrada:</span>
                  <span className="text-[var(--ink)]">{cal.hora_entrada_min} – {cal.hora_entrada_max}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[var(--ink-3)] w-24 flex-shrink-0">Salida:</span>
                  <span className="text-[var(--ink)]">{cal.hora_salida_min} – {cal.hora_salida_max}</span>
                </div>
                {cal.pausa_obligatoria ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[var(--ink-3)] w-24 flex-shrink-0">Pausa:</span>
                    <span className="badge badge-warning">
                      Obligatoria {cal.duracion_pausa_min} min
                      {cal.dias_pausa_obligatoria && cal.dias_pausa_obligatoria !== cal.dias_laborables
                        ? ` · ${formatDias(cal.dias_pausa_obligatoria)}`
                        : ''}
                    </span>
                  </div>
                ) : null}
              </div>

              <div className="mt-3 pt-3 border-t flex gap-4 text-xs text-[var(--ink-4)]">
                <span>{cal.num_grupos || 0} grupo(s)</span>
                <span>{cal.num_empleados_directo || 0} empleado(s) directo</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-semibold">{editando ? 'Editar calendario' : 'Nuevo calendario laboral'}</h3>
              <button onClick={() => setModal(false)} className="text-[var(--ink-4)] hover:text-[var(--ink-2)]"><FiX size={20} /></button>
            </div>
            <form onSubmit={guardar} className="p-5 space-y-4">
              <div>
                <label className="label">Nombre *</label>
                <input required value={form.nombre} onChange={e => setForm(p => ({...p, nombre: e.target.value}))} className="input w-full" placeholder="Ej: Jornada Completa Madrid" />
              </div>
              <div>
                <label className="label">Descripción</label>
                <input value={form.descripcion} onChange={e => setForm(p => ({...p, descripcion: e.target.value}))} className="input w-full" placeholder="Descripción opcional" />
              </div>

              {/* Días laborables + horas integrado — grid 2 columnas */}
              <div>
                <label className="label">Días laborables y horas de jornada</label>
                <div className="grid grid-cols-2 gap-1 mt-2">
                  {HORAS_POR_DIA.map(({ key, num, label }) => {
                    const activo = diasSeleccionados.includes(num)
                    const letra = DIAS_SEMANA.find(d => d.iso === num)?.nombre || label[0]
                    return (
                      <div
                        key={key}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                          activo ? 'bg-white border-blue-200' : 'bg-[var(--bg-2)] border-[var(--line)]'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleDia(num)}
                          className={`w-7 h-7 rounded-md text-xs font-bold flex-shrink-0 transition-all ${
                            activo ? 'bg-blue-500 text-white' : 'bg-[var(--line)] text-[var(--ink-4)] hover:bg-[var(--line-2)]'
                          }`}
                        >
                          {letra}
                        </button>
                        <span className={`text-xs flex-1 ${activo ? 'text-[var(--ink-2)] font-medium' : 'text-[var(--ink-4)]'}`}>
                          {label}
                        </span>
                        {activo ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              min="0" max="24" step="0.5"
                              value={form[key]}
                              onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                              placeholder="—"
                              className="w-12 text-xs text-center border border-[var(--line)] rounded-md px-1 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                            <span className="text-xs text-[var(--ink-4)]">h</span>
                          </div>
                        ) : (
                          <span className="text-[10px] text-[var(--ink-4)]">—</span>
                        )}
                      </div>
                    )
                  })}
                </div>
                <p className="text-xs text-[var(--ink-4)] mt-1.5">Ej: 8 = 8h, 7.5 = 7h 30m</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Entrada mínima (más temprana)</label>
                  <input type="time" value={form.hora_entrada_min} onChange={e => setForm(p => ({...p, hora_entrada_min: e.target.value}))} className="input w-full" />
                </div>
                <div>
                  <label className="label">Entrada máxima (más tardía)</label>
                  <input type="time" value={form.hora_entrada_max} onChange={e => setForm(p => ({...p, hora_entrada_max: e.target.value}))} className="input w-full" />
                </div>
                <div>
                  <label className="label">Salida mínima (más temprana)</label>
                  <input type="time" value={form.hora_salida_min} onChange={e => setForm(p => ({...p, hora_salida_min: e.target.value}))} className="input w-full" />
                </div>
                <div>
                  <label className="label">Salida máxima (más tardía)</label>
                  <input type="time" value={form.hora_salida_max} onChange={e => setForm(p => ({...p, hora_salida_max: e.target.value}))} className="input w-full" />
                </div>
              </div>

              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-[var(--ink-2)]">Pausa obligatoria por día</p>
                    <p className="text-xs text-[var(--ink-4)]">Marca los días en que la pausa es obligatoria. Si el empleado no la registra al salir esos días, se le pedirá añadirla.</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button type="button" onClick={() => setDiasPausaObligatoria([...diasSeleccionados])}
                      className="text-[11px] px-2 py-1 rounded border border-[var(--line)] text-[var(--ink-3)] hover:bg-[var(--bg-2)]">Todos los laborables</button>
                    <button type="button" onClick={() => setDiasPausaObligatoria([])}
                      className="text-[11px] px-2 py-1 rounded border border-[var(--line)] text-[var(--ink-3)] hover:bg-[var(--bg-2)]">Ninguno</button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {DIAS_SEMANA.map(d => {
                    const esLaborable = diasSeleccionados.includes(d.iso)
                    const activo = diasPausaObligatoria.includes(d.iso)
                    return (
                      <button
                        key={d.iso}
                        type="button"
                        disabled={!esLaborable}
                        onClick={() => toggleDiaPausa(d.iso)}
                        title={esLaborable ? d.largo : `${d.largo} (no laborable)`}
                        className={`w-9 h-9 rounded-md text-xs font-bold transition-all ${
                          !esLaborable
                            ? 'bg-[var(--bg-2)] text-[var(--ink-4)] opacity-40 cursor-not-allowed'
                            : activo
                              ? 'bg-amber-500 text-white'
                              : 'bg-[var(--line)] text-[var(--ink-3)] hover:bg-[var(--line-2)]'
                        }`}
                      >
                        {d.nombre}
                      </button>
                    )
                  })}
                </div>
                {diasPausaObligatoria.length > 0 && (
                  <div>
                    <label className="label">Duración mínima de pausa (minutos)</label>
                    <input type="number" min="5" max="240" value={form.duracion_pausa_min} onChange={e => setForm(p => ({...p, duracion_pausa_min: parseInt(e.target.value) || 0}))} className="input w-full" />
                    <p className="text-xs text-[var(--ink-4)] mt-1">Aplica a todos los días con pausa obligatoria.</p>
                  </div>
                )}
                <p className="text-[11px] text-[var(--ink-4)] border-t pt-2">
                  En los días sin pausa obligatoria, si el empleado hace una pausa igualmente, esta cuenta como tiempo no trabajado (se descuenta de la jornada).
                </p>
              </div>

              {/* Umbral horas extra */}
              <div className="border rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[var(--ink-2)]">Umbral para horas extra</p>
                    <p className="text-xs text-[var(--ink-4)]">Minutos mínimos de exceso sobre la jornada para proponer una solicitud de hora extra</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <input
                      type="number"
                      min="5" max="240"
                      value={form.umbral_horas_extra}
                      onChange={e => setForm(p => ({ ...p, umbral_horas_extra: parseInt(e.target.value) || 30 }))}
                      className="input w-20 text-center"
                    />
                    <span className="text-sm text-[var(--ink-3)] whitespace-nowrap">min</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModal(false)} className="btn btn-secondary flex-1">Cancelar</button>
                <button type="submit" className="btn btn-primary flex-1">{editando ? 'Guardar cambios' : 'Crear calendario'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
